import {queryForRepos} from '@github-ui/one-copilot-composer/task-chat-input-toolbar/TaskChatInputToolbar'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import type {APITaskGroup} from '../types/api-task'
import type {SidebarRepository} from '../types/sidebar-repository'
import {agentGroupedTasksQueryKey} from './use-grouped-tasks-data'

export const sidebarRepositoryQueryKey = (nwo: string) => ['sidebar-repository', nwo.toLowerCase()]

export function useSidebarRepositoryFilter(nwo: string | undefined, initialGroups?: APITaskGroup[]) {
  const queryClient = useQueryClient()
  const enabled = Boolean(nwo)
  const normalizedNwo = nwo?.toLowerCase()
  const fromServer = initialGroups
    ?.map(group => group.repository)
    .find(repo => repo?.id && repo.id > 0 && repo.full_name?.toLowerCase() === normalizedNwo)
  const cached = queryClient
    .getQueriesData<{groups: Array<{nwo?: string; repoId?: number}>}>({queryKey: agentGroupedTasksQueryKey})
    .flatMap(([, data]) => data?.groups ?? [])
    .find(group => group.repoId && group.repoId > 0 && group.nwo?.toLowerCase() === normalizedNwo)
  const initialData: SidebarRepository | undefined = fromServer
    ? {id: fromServer.id, nameWithOwner: fromServer.full_name}
    : cached?.repoId && cached.nwo
      ? {id: cached.repoId, nameWithOwner: cached.nwo}
      : undefined

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Repository identity is a short-lived, URL-scoped picker lookup.
  const query = useQuery({
    queryKey: sidebarRepositoryQueryKey(nwo ?? ''),
    queryFn: async (): Promise<SidebarRepository> => {
      const repositories = await queryForRepos(nwo, undefined, undefined, {rejectOnHttpError: true})
      const match = repositories.find(
        repo => repo.nameWithOwner.toLowerCase() === nwo?.toLowerCase() && repo.id && repo.id > 0,
      )
      if (!match?.id) throw new Error('Repository not found')
      return {id: match.id, nameWithOwner: match.nameWithOwner}
    },
    enabled,
    initialData: enabled ? initialData : undefined,
    staleTime: Infinity,
    retry: false,
  })

  return {
    repository: enabled ? query.data : undefined,
    isLoading: enabled && query.isLoading,
    isError: enabled && query.isError,
  }
}
